import type { GetCommonTemplatesWithValuesBffResponse, GetProjectTemplatesWithValuesBffResponse, ProjectResource } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import type { TemplatesWarnings } from "~/areas/projects/components/Variables/TenantVariables/ProjectTenantVariablesNavigationTabs";
import { useProjectTemplatesFilterState } from "~/areas/projects/components/Variables/TenantVariables/useProjectTemplatesFilterState";
import { usePageState } from "~/areas/tenants/components/Paging/usePageState";
import { repository } from "~/clientInstance";
import { type DoBusyTask, useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import RequestRaceConditioner from "~/utils/RequestRaceConditioner/index";
interface Actions {
    setPageState: (newPageNumber: number, newPageSize: number) => void;
    setFilter: (newFilter: ProjectTenantVariablesFilterState) => void;
    resetFilter: (newFilter: Partial<ProjectTenantVariablesFilterState>) => void;
    onSaveProjectTemplates: () => Promise<void>;
}
export interface ProjectTemplatesState {
    data: GetProjectTemplatesWithValuesBffResponse | null;
    warnings: TemplatesWarnings;
    pageSize: number;
    filterState: ProjectTenantVariablesFilterState;
    actions: Actions;
}
const pagingSelectionKey = "octoProjectTenantVariablesPagingSelection";
export function useProjectTemplatesState(project: ProjectResource, doBusyTask: DoBusyTask): ProjectTemplatesState {
    const [data, setData] = useState<GetProjectTemplatesWithValuesBffResponse | null>(null);
    const [warnings, setWarnings] = useState<TemplatesWarnings>({ projectTemplates: false, commonTemplates: false });
    const [pageState, setPageState] = usePageState(pagingSelectionKey);
    const [filterState, setFilterState] = useProjectTemplatesFilterState();
    const [isInitialPageLoad, setIsInitialPageLoad] = useState<boolean>(true);
    const raceConditionerRef: Readonly<React.MutableRefObject<RequestRaceConditioner>> = React.useRef(new RequestRaceConditioner());
    const actions: Actions = {
        setPageState: (newPageNumber, newPageSize) => {
            setFilterState({ ...filterState, pageNumber: newPageNumber });
            setPageState(filterState.pageNumber, newPageSize);
        },
        setFilter: (newFilter) => {
            setFilterState({ ...newFilter, pageNumber: 1 });
        },
        resetFilter: (newFilter) => {
            setFilterState({ ...defaultFilter, ...newFilter });
        },
        onSaveProjectTemplates: async () => {
            await doBusyTask(async () => {
                await raceConditionerRef.current.avoidStaleResponsesForRequest(repository.Projects.getProjectTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState), (response) => {
                    setData(response);
                });
            });
        },
    };
    useDoBusyTaskEffect(doBusyTask, async () => {
        const loadProjectTemplates = () => repository.Projects.getProjectTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState);
        const updateDataState = (projectResponse: GetProjectTemplatesWithValuesBffResponse | null, commonResponse: GetCommonTemplatesWithValuesBffResponse | null) => {
            setData(projectResponse);
            setWarnings((previous) => ({ projectTemplates: projectResponse?.AnyMissingValues ?? previous.projectTemplates, commonTemplates: commonResponse?.AnyMissingValues ?? previous.commonTemplates }));
        };
        if (isInitialPageLoad) {
            const [projectResponse, commonResponse] = await Promise.all([
                loadProjectTemplates(),
                isFeatureToggleEnabled("CommonVariableScopingFeatureToggle")
                    ? repository.Projects.getScopedCommonTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState)
                    : repository.Projects.getCommonTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState),
            ]);
            updateDataState(projectResponse, commonResponse);
            setIsInitialPageLoad(false);
        }
        else {
            await raceConditionerRef.current.avoidStaleResponsesForRequest(loadProjectTemplates(), (response) => updateDataState(response, null));
        }
    }, [project, filterState, pageState.pageSize]);
    return { data, warnings, pageSize: pageState.pageSize, filterState, actions };
}
const defaultFilter: ProjectTenantVariablesFilterState = {
    filterByName: undefined,
    filterByEnvironments: [],
    filterByTenants: [],
    filterByTags: [],
    filterByValueStatus: undefined,
    filterByTemplateId: undefined,
    filterByLibraryVariableSetIds: [],
    filterByProjectIds: [],
    pageNumber: 1,
};
